import { useAppForm } from '@massilia/features'
import { useSendResetPasswordEmailMutation } from '@massilia/queries'
import { Button } from '@massilia/ui'
import { revalidateLogic, standardSchemaValidators } from '@tanstack/react-form'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Check } from 'lucide-react'
import { useState } from 'react'

import { AuthErrorComponent } from '../../components/login/auth-error-component'
import { resolveAuthErrorMessage } from '../../lib/auth-error-message'
import { forgottenPasswordSchema } from '../../lib/schemas'

export const Route = createFileRoute('/_auth/mot-de-passe-oublie')({
  component: ForgottenPasswordComponent,
})

function ForgottenPasswordComponent() {
  const [submitError, setSubmitError] = useState<null | string>(null)
  const form = useAppForm({
    defaultValues: {
      email: '',
    },
    listeners: {
      onChange: () => {
        if (submitError) {
          setSubmitError(null)
        }
      },
    },
    onSubmit: async ({ value }) => {
      try {
        await sendResetPasswordEmail(value)
        setToDisplayEmailInMessage(value.email)
        setShouldShowMessage(true)
      } catch (error) {
        setShouldShowMessage(false)
        setSubmitError(resolveAuthErrorMessage(error))
      }
    },
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'blur',
    }),
    validators: {
      onDynamic: ({ value }) =>
        standardSchemaValidators.validate(
          { validationSource: 'form', value },
          forgottenPasswordSchema
        ),
    },
  })
  const [toDisplayEmailInMessage, setToDisplayEmailInMessage] = useState('')
  const [shouldShowMessage, setShouldShowMessage] = useState(false)

  const { mutateAsync: sendResetPasswordEmail } = useSendResetPasswordEmailMutation()

  return (
    <>
      <h1 className="text-gray-light-900 text-center text-xl font-semibold">
        Mot de passe oublié ?
      </h1>
      <form
        className="flex w-full flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          void form.handleSubmit()
        }}
      >
        <form.AppField name="email">
          {(field) => <field.TextField label="Adresse email" placeholder="email@example.com" />}
        </form.AppField>
        {submitError && <AuthErrorComponent message={submitError} />}
        {shouldShowMessage && (
          <div className="flex w-full items-center gap-5 rounded-md border border-[#16A34A] p-4">
            <div className="flex items-center justify-center rounded-full border-2 border-[#16A34A20] p-1">
              <div className="flex items-center justify-center rounded-full border-2 border-[#16A34A40] p-1">
                <Check className="h-4 w-4 text-[#16A34A]" />
              </div>
            </div>
            <p className="break min-w-0 text-sm font-semibold text-[#16A34A]">
              Nous avons envoyé un email de réinitialisation à l&apos;adresse{' '}
              <span className="break-all">{toDisplayEmailInMessage}</span>. Veuillez vérifier vos
              spams.
            </p>
          </div>
        )}
        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(isSubmitting) => (
            <Button
              className="bg-brand-600 hover:bg-brand-800 w-full px-8 py-2 text-white"
              disabled={isSubmitting}
              type="submit"
            >
              {isSubmitting ? 'Envoi en cours...' : 'Réinitialiser le mot de passe'}
            </Button>
          )}
        </form.Subscribe>
        <form.Subscribe selector={(state) => state.isSubmitted && !state.isValid}>
          {(hasValidationErrors) =>
            hasValidationErrors ? (
              <AuthErrorComponent message="Veuillez renseigner une adresse email valide." />
            ) : null
          }
        </form.Subscribe>
      </form>
      <Link to="/connexion">
        <Button className="text-brand-700 w-full hover:underline" variant="none">
          Retour
        </Button>
      </Link>
    </>
  )
}
